import React, { useState, useEffect } from "react";
import { apiService } from "../../services/apiService";
import { PortfolioCompany } from "../../types/portfolio";
import InlineSpinner from "../common/InlineSpinner";
import { useToast, ToastContainer } from "../common/Toast";

interface CompanyDetailModalProps {
  companyId: string;
  onClose: () => void;
}

const CompanyDetailModal: React.FC<CompanyDetailModalProps> = ({
  companyId,
  onClose,
}) => {
  const [company, setCompany] = useState<PortfolioCompany | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const { toasts, removeToast, success, error: errorToast } = useToast();

  useEffect(() => {
    loadCompanyDetails();
  }, [companyId]);

  const loadCompanyDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getCompanyDetails(companyId);
      if (response.success) {
        setCompany(response.data);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "#10b981";
      case "inactive":
        return "#6b7280";
      case "churned":
        return "#ef4444";
      default:
        return "#667eea";
    }
  };

  const handleResendInvite = async () => {
    setResending(true);
    try {
      await apiService.resendPortfolioInvite(companyId);
      success("Invitation resent successfully!");
    } catch (err: any) {
      errorToast(err.message || "Failed to resend invitation");
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#1a1a1a",
          border: "1px solid #333",
          borderRadius: "12px",
          maxWidth: "700px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.5rem 2rem",
            borderBottom: "1px solid #333",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ margin: 0, color: "#fff", fontSize: "1.5rem" }}>
            Company Details
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#9ca3af",
              cursor: "pointer",
              fontSize: "1.5rem",
              padding: "0.25rem",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "2rem" }}>
          <ToastContainer toasts={toasts} onRemove={removeToast} />
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "3rem",
                color: "#d1d5db",
              }}
            >
              <InlineSpinner />
              <span style={{ marginLeft: "0.75rem" }}>Loading details...</span>
            </div>
          ) : error ? (
            <div
              style={{
                background: "#ef4444",
                padding: "1rem",
                borderRadius: "6px",
                color: "#fff",
              }}
            >
              {error}
            </div>
          ) : company ? (
            <>
              {company.status === "invited" ? (
                /* Invited Company State */
                <div>
                  {/* Company Name & Email */}
                  <div style={{ marginBottom: "1.25rem" }}>
                    <h3
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        margin: "0 0 0.5rem 0",
                        color: "#fff",
                      }}
                    >
                      {company.company_name}
                    </h3>
                    {company.user_email && (
                      <p
                        style={{
                          margin: 0,
                          color: "#9ca3af",
                          fontSize: "0.9375rem",
                        }}
                      >
                        {company.user_email}
                      </p>
                    )}
                  </div>

                  {/* Pending Invite Message */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
                      border: "1px solid #667eea40",
                      borderRadius: "10px",
                      padding: "1.25rem",
                      textAlign: "center",
                      marginBottom: "1.25rem",
                    }}
                  >
                    <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
                      📨
                    </div>
                    <h4
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: "#fff",
                        margin: "0 0 0.5rem 0",
                      }}
                    >
                      Invitation Pending
                    </h4>
                    <p
                      style={{
                        fontSize: "0.875rem",
                        color: "#9ca3af",
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      This company hasn&apos;t accepted the invitation yet. Full
                      details will appear once they join.
                    </p>
                  </div>

                  {/* Invite Info */}
                  <div
                    style={{
                      background: "#0a0a0a",
                      padding: "1rem",
                      borderRadius: "8px",
                      border: "1px solid #2a2a2a",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#d1d5db",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ color: "#9ca3af" }}>Invited:</span>
                        <span>{formatDate(company.created_at)}</span>
                      </div>
                      {(company as any).invite_expires_at && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ color: "#9ca3af" }}>Expires:</span>
                          <span>
                            {formatDate((company as any).invite_expires_at)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Resend Invite Button */}
                  <button
                    onClick={handleResendInvite}
                    disabled={resending}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      marginTop: "1rem",
                      background: resending ? "#4b5563" : "#667eea",
                      border: "none",
                      borderRadius: "8px",
                      color: "#fff",
                      cursor: resending ? "not-allowed" : "pointer",
                      fontSize: "0.9375rem",
                      fontWeight: 600,
                      transition: "background 0.2s",
                    }}
                  >
                    {resending ? "Resending..." : "↻ Resend Invitation"}
                  </button>
                </div>
              ) : (
                /* Active/Normal Company State */
                <div>
                  {/* Company Name & Email */}
                  <div style={{ marginBottom: "2rem" }}>
                    <h3
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        margin: "0 0 0.5rem 0",
                        color: "#fff",
                      }}
                    >
                      {company.company_name}
                    </h3>
                    {company.user_email && (
                      <p
                        style={{
                          margin: 0,
                          color: "#9ca3af",
                          fontSize: "1rem",
                        }}
                      >
                        {company.user_email}
                      </p>
                    )}
                    {company.user_full_name && (
                      <p
                        style={{
                          margin: "0.25rem 0 0 0",
                          color: "#9ca3af",
                          fontSize: "0.875rem",
                        }}
                      >
                        Contact: {company.user_full_name}
                      </p>
                    )}
                  </div>

                  {/* Stats Grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "1rem",
                      marginBottom: "2rem",
                    }}
                  >
                    <div
                      style={{
                        background: "#0a0a0a",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: "1px solid #2a2a2a",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#9ca3af",
                          marginBottom: "0.5rem",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}
                      >
                        Status
                      </div>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "0.375rem 0.875rem",
                          borderRadius: "12px",
                          fontSize: "0.9375rem",
                          fontWeight: 600,
                          background: getStatusColor(company.status) + "20",
                          color: getStatusColor(company.status),
                        }}
                      >
                        {company.status}
                      </span>
                    </div>

                    <div
                      style={{
                        background: "#0a0a0a",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: "1px solid #2a2a2a",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#9ca3af",
                          marginBottom: "0.5rem",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}
                      >
                        Integrations
                      </div>
                      <div
                        style={{
                          fontSize: "1.5rem",
                          fontWeight: 700,
                          color: "#fff",
                        }}
                      >
                        {company.integrations_count || 0}
                      </div>
                    </div>

                    <div
                      style={{
                        background: "#0a0a0a",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: "1px solid #2a2a2a",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#9ca3af",
                          marginBottom: "0.5rem",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}
                      >
                        Last Update
                      </div>
                      <div style={{ fontSize: "0.9375rem", color: "#d1d5db" }}>
                        {formatDate(company.last_update)}
                      </div>
                    </div>

                    <div
                      style={{
                        background: "#0a0a0a",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: "1px solid #2a2a2a",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#9ca3af",
                          marginBottom: "0.5rem",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}
                      >
                        Next Update
                      </div>
                      <div style={{ fontSize: "0.9375rem", color: "#d1d5db" }}>
                        {formatDate(company.next_update)}
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div
                    style={{
                      background: "#0a0a0a",
                      padding: "1rem",
                      borderRadius: "8px",
                      border: "1px solid #2a2a2a",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#9ca3af",
                        marginBottom: "0.75rem",
                        textTransform: "uppercase",
                        fontWeight: 600,
                      }}
                    >
                      Account Info
                    </div>
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#d1d5db",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                      }}
                    >
                      <div>
                        <span style={{ color: "#9ca3af" }}>Created:</span>{" "}
                        {formatDate(company.created_at)}
                      </div>
                      <div>
                        <span style={{ color: "#9ca3af" }}>Last Modified:</span>{" "}
                        {formatDate(company.updated_at)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailModal;
